import { diasEntre as diasEntreFechas, soloFecha } from './calendar';
import type { Frescura, Fuente, FuenteResumen, ISODate, Precio } from './types';

/**
 * Frescura de las fuentes y hora de referencia (America/Caracas, UTC-4 sin horario de verano).
 * Misma semántica que el servidor (docs/CONTRATO_API.md §2.9):
 *   al_dia: edad ≤ 1,5 × frecuencia · atrasada: ≤ 3 × · desactualizada: > 3 × · sin_datos.
 */
export const TIMEZONE_CARACAS = 'America/Caracas';
/** Frecuencia (días) cuando la fuente no declara una reconocible ("irregular", vacío…). */
export const FRECUENCIA_POR_DEFECTO = 14;

export { diasEntreFechas as diasEntre };

const CARACAS_OFFSET_MS = 4 * 3_600_000;

let caracasFormatter: Intl.DateTimeFormat | null | undefined;

function getFormatter(): Intl.DateTimeFormat | null {
  if (caracasFormatter !== undefined) return caracasFormatter;
  try {
    caracasFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE_CARACAS,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    caracasFormatter = null;
  }
  return caracasFormatter;
}

/** Día calendario de Caracas para un instante (Date o ISO con zona). null si el instante no es válido. */
export function fechaCaracas(instante: Date | string | number): ISODate | null {
  const date = instante instanceof Date ? instante : new Date(instante);
  if (Number.isNaN(date.getTime())) return null;
  const formatter = getFormatter();
  if (!formatter) return new Date(date.getTime() - CARACAS_OFFSET_MS).toISOString().slice(0, 10);
  const part = (type: string): string =>
    formatter.formatToParts(date).find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** "Hoy" en Caracas ('YYYY-MM-DD'). `ahora` permite fijar el instante en pruebas. */
export function hoyCaracas(ahora: Date | string | number = new Date()): ISODate {
  return fechaCaracas(ahora) ?? new Date(Date.now() - CARACAS_OFFSET_MS).toISOString().slice(0, 10);
}

const normalizar = (texto: string): string =>
  texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

const POR_PALABRA: ReadonlyArray<readonly [RegExp, number]> = [
  [/\b(diari[oa]s?|daily|todos los dias|cada dia)\b/, 1],
  [/\b(semanal(es)?|weekly|cada semana|semana)\b/, 7],
  [/\b(quincenal(es)?|biweekly|fortnightly|cada quincena|quincena)\b/, 15],
  [/\b(mensual(es)?|monthly|cada mes|mes)\b/, 30],
];
const CADA_N = /\bcada\s+(\d{1,3})\s*(dias?|semanas?|meses|mes)\b/;

/** Días entre actualizaciones según el texto libre de la fuente (desconocido/irregular → 14). */
export function frecuenciaDias(texto: string | null | undefined): number {
  if (!texto) return FRECUENCIA_POR_DEFECTO;
  const limpio = normalizar(texto);
  const cadaN = CADA_N.exec(limpio);
  if (cadaN) {
    const factor = cadaN[2]?.startsWith('semana') ? 7 : cadaN[2]?.startsWith('mes') ? 30 : 1;
    return Math.max(1, Number(cadaN[1]) * factor);
  }
  for (const [patron, dias] of POR_PALABRA) if (patron.test(limpio)) return dias;
  return FRECUENCIA_POR_DEFECTO;
}

/** Fecha efectiva de una observación: fin del período si la fuente reporta semana/quincena. */
export const fechaObs = (p: { fecha: ISODate; fechaFin?: ISODate | null }): ISODate =>
  p.fechaFin ?? p.fecha;

/** Edad en días (nunca negativa: una fecha futura cuenta como 0). null si alguna fecha es inválida. */
export function edadDias(ultimoDato: ISODate | null | undefined, hoy: ISODate): number | null {
  if (!ultimoDato) return null;
  const dias = diasEntreFechas(ultimoDato, hoy);
  return Number.isNaN(dias) ? null : Math.max(0, dias);
}

export function calcularFrescura(
  ultimoDato: ISODate | null | undefined,
  frecuencia: number,
  hoy: ISODate,
): Frescura {
  const edad = edadDias(ultimoDato, hoy);
  if (edad === null) return 'sin_datos';
  const base = Number.isFinite(frecuencia) && frecuencia > 0 ? frecuencia : FRECUENCIA_POR_DEFECTO;
  if (edad <= base * 1.5) return 'al_dia';
  return edad <= base * 3 ? 'atrasada' : 'desactualizada';
}

const unicos = (valores: Array<string | null>): string[] =>
  [...new Set(valores.filter((v): v is string => Boolean(v)))].sort((a, b) =>
    a.localeCompare(b, 'es'),
  );

/**
 * Resumen por fuente calculado en el cliente (modo básico: sin /v1/fuentes/detalle).
 * `ultimoDato` = el mayor entre lo que declara la fuente y lo observado en los precios.
 */
export function resumirFuentes(
  fuentes: readonly Fuente[],
  precios: readonly Precio[],
  hoy: ISODate,
): FuenteResumen[] {
  return fuentes.map((fuente) => {
    const propios = precios.filter((p) => p.fuente === fuente.clave);
    const observado = propios.map(fechaObs).sort().at(-1) ?? null;
    const declarado = soloFecha(fuente.ultimoDato);
    const ultimoDato =
      [declarado, observado]
        .filter((v): v is ISODate => v !== null)
        .sort()
        .at(-1) ?? null;
    const frecuencia = frecuenciaDias(fuente.frecuencia);
    return {
      ...fuente,
      ultimoDato,
      frescura: calcularFrescura(ultimoDato, frecuencia, hoy),
      diasDesdeUltimo: edadDias(ultimoDato, hoy),
      frecuenciaDias: frecuencia,
      filas: propios.length,
      rubros: unicos(propios.map((p) => p.rubro)),
      regiones: unicos(propios.map((p) => p.region)),
      activa: true,
    };
  });
}
